import { Controller } from "@hotwired/stimulus"
import Uppy from "@uppy/core"
import Dashboard from "@uppy/dashboard"
import ImageEditor from "@uppy/image-editor"
import Tus from "@uppy/tus"
import Russian from '@uppy/locales/lib/ru_RU'
import mammoth from 'mammoth'
import * as pdfjsLib from 'pdfjs-dist'
import '@uppy/image-editor/dist/style.css'

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js'
export default class extends Controller {
  static targets = ["modal", "title", "loading", "imageContainer", "image", "pdfContainer", "pdf", "docContainer", "docName", "docLink", "startButton", "closeButton"]
  static values = {
    url: String,
    fileType: String,
  }

  // Instance properties
  fileType = ""
  fileName = ""

  connect() {
    if (this.modalTarget) {
      this.modal = new bootstrap.Modal(this.modalTarget, {
        keyboard: true,
        focus: true
      })

      // Add cleanup on modal hide
      this.modalTarget.addEventListener('hidden.bs.modal', () => {
        this.cleanup()
      })
    }
  }

  cleanup() {
    // Clean up PDF document
    if (this.pdfDocument) {
      this.pdfDocument.destroy()
      this.pdfDocument = null
    }

    // Reset state
    this.currentPage = 1
    this.scale = 1.0

    // Return focus to the element that triggered the modal
    if (this.lastActiveElement) {
      this.lastActiveElement.focus()
      this.lastActiveElement = null
    }
  }

  show(event) {
    // Store the element that triggered the modal to return focus later
    this.lastActiveElement = document.activeElement

    // Show loading indicator
    this.showLoading(true)

    // Reset all content containers
    this.resetContainers()

    const targetElement = event.currentTarget || event.target

    const url = targetElement.dataset.url
    this.fileType = targetElement.dataset.fileType || ""
    this.fileName = event.currentTarget.dataset.fileName || ""

    if (url && this.fileName) {
      this.renderDocumentByType(url, this.fileName)
    }

    // Show the modal
    this.modal.show()

    // Set focus on close button after modal is shown
    this.modalTarget.addEventListener('shown.bs.modal', () => {
      this.closeButtonTarget.focus()
    }, { once: true })
  }

  // Helper methods
  showLoading(show) {
    if (show) {
      this.loadingTarget.classList.remove('d-none')
    } else {
      this.loadingTarget.classList.add('d-none')
    }
  }

  resetContainers() {
    this.imageContainerTarget.classList.add('d-none')
    this.pdfContainerTarget.classList.add('d-none')
    this.docContainerTarget.classList.add('d-none')
  }

  showImage(url) {
    this.imageTarget.src = url
    this.imageTarget.style.maxWidth = '100%'
    this.imageTarget.style.maxHeight = '90vh'
    this.imageTarget.style.objectFit = 'contain'
    this.imageTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)'

    // Handle image load error
    this.imageTarget.onerror = () => {
      this.imageContainerTarget.innerHTML = `
        <div class="text-center text-danger" style="padding: 40px;">
          Image failed to load
          <br />
          <button class="btn btn-link p-0" data-action="click->document-modal#handleDownload" data-download-url="${url}" data-download-filename="${this.fileName}">${this.fileName}</button>
        </div>
      `
    }

    this.showLoading(false)
    this.imageContainerTarget.classList.remove('d-none')
    this.titleTarget.textContent = 'Изображение ' + this.fileName
    this.startButtonTarget.classList.add('d-none')
  }

  showPdf(url) {
    this.showLoading(true)
    this.pdfContainerTarget.classList.remove('d-none')
    this.titleTarget.textContent = 'PDF - ' + this.fileName
    this.startButtonTarget.classList.add('d-none')

    // Clear any existing content
    this.pdfContainerTarget.innerHTML = `
      <div id="pdf-viewer-container" style="width: 100%; height: 70vh; overflow: auto; border: 1px solid #ccc;">
        <div id="pdf-loading" class="text-center p-4">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading PDF...</span>
          </div>
          <p>Loading PDF...</p>
        </div>
      </div>
      <div class="pdf-controls mt-2 text-center">
        <button id="pdf-prev" class="btn btn-secondary btn-sm me-2" disabled>Previous</button>
        <span id="pdf-page-info">Page 1 of 1</span>
        <button id="pdf-next" class="btn btn-secondary btn-sm ms-2" disabled>Next</button>
        <div class="mt-2">
          <button id="pdf-zoom-in" class="btn btn-outline-secondary btn-sm me-1">Zoom In</button>
          <button id="pdf-zoom-out" class="btn btn-outline-secondary btn-sm me-1">Zoom Out</button>
          <button id="pdf-fit-width" class="btn btn-outline-secondary btn-sm">Fit Width</button>
        </div>
      </div>
    `

    // Load and render PDF
    this.loadPdfDocument(url)
  }

  showDoc(url) {
    // Set the doc download link
    this.docLinkTarget.href = url

    // Set the download attribute with the fileName to control download filename
    this.docLinkTarget.setAttribute('download', this.fileName)

    // Set doc name (extract from URL)
    this.docNameTarget.textContent = this.fileName

    // Hide loading and show doc container
    this.showLoading(false)
    this.docContainerTarget.classList.remove('d-none')

    // Set title based on file type
    const fileExtension = this.extractFileExtension(this.fileName).toLowerCase()
    if (['pdf', 'doc', 'docx', 'txt', 'html'].includes(fileExtension)) {
      this.titleTarget.textContent = 'Просмотр документа ' + this.fileName
    } else {
      this.titleTarget.textContent = 'Файл ' + this.fileName
    }

    // Show start button for docs
    this.startButtonTarget.classList.remove('d-none')
  }

  addImage(editor) {
    const locale = window.locale == "ru" ? Russian : 'en_EN'
    const imageWidth = 300;
    const imageHeight = 300;

    const uppy = new Uppy({
      restrictions: {
        maxNumberOfFiles: 1,
        // allowedFileTypes: ['image/*']
      },
      autoProceed: false,
      locale: locale
    })

    // Store Uppy instance as a property of this controller
    this.uppyInstance = uppy;

    uppy.use(Tus, { endpoint: window.upload_endpoint, chunkSize: 5 * 1024 * 1024 })
    uppy.use(Dashboard, {
      proudlyDisplayPoweredByUppy: false,
      autoOpenFileEditor: true,
      hideCancelButton: true
    })
    uppy.use(ImageEditor, {
      target: Dashboard,
      quality: 1,
      cropperOptions: {
        viewMode: 1,
        background: true,
        // autoCropArea: 0.8,
        responsive: true,
        // aspectRatio: 1 / 1,
        croppedCanvasOptions: {
          // widimageWidthth: imageWidth,
          // height: imageHeight
        },
      },
      actions: {
        revert: true,
        rotate: true,
        granularRotate: true,
        flip: true,
        zoomIn: true,
        zoomOut: true,
        cropSquare: false,
        cropWidescreen: false,
        cropWidescreenVertical: false,
      },
    })

    uppy.on('upload-success', (file, response) => {
      // Get the dimensions from the file's metadata after cropping
      const width = file.meta.width || imageWidth;
      const height = file.meta.height || imageHeight;

      let fileData = {
        title: file.name,
        file_orig_name: file.name,
        file_url: response.uploadURL,
        file_size: file.size,
        file_type: file.meta.type,
        width: width,
        height: height
      }
      this.createFile(fileData, editor, uppy);
      // The modal will be closed in createFile after ensuring the URL is accessible
    });

    uppy.getPlugin('Dashboard').openModal();
  }

  createFile(fileData, editor, uppy) {
    let fileDataCreate = fileData;
    delete fileDataCreate.width;
    delete fileDataCreate.height;
    const parameters = JSON.stringify({ file: fileDataCreate });
    const token = document.getElementsByName("csrf-token")[0].content;

    // Show a waiting overlay
    this.showWaitingOverlay('Ожидание загрузки...');

    fetch('/api/v1/files.json', {
      body: parameters,
      method: 'POST',
      credentials: "include",
      headers: {
        'Content-Type': 'application/json',
        "X-CSRF-Token": token
      },
    })
      .then(response => {
        return response.json();
      })
      .then(data => {
        // Check if URL is accessible before inserting
        this.checkImageUrlAccessibility(data.url)
          .then(isAccessible => {
            // Hide waiting overlay
            this.hideWaitingOverlay();

            if (isAccessible) {
              // Insert the image with the width and height from the cropped image
              // editor.insertContent(`<img src="${data.url}" width="${fileData.width}" height="${fileData.height}">`);
              console.log("Image URL is accessible:", data.url);
            } else {
              console.error("Image URL is not accessible:", data.url);
              editor.notificationManager.open({
                text: "The uploaded image URL is not accessible. Please try again.",
                type: "error"
              });
            }
            // Always close the modal, even if URL is not accessible
            if (uppy && uppy.getPlugin('Dashboard')) {
              uppy.getPlugin('Dashboard').closeModal();
            }
          })
          .catch(error => {
            // Hide waiting overlay
            this.hideWaitingOverlay();

            console.error("Error checking image URL:", error);
            // Insert image with dimensions
            editor.insertContent(`<img src="${data.url}" width="${fileData.width}" height="${fileData.height}">`);
            if (uppy && uppy.getPlugin('Dashboard')) {
              uppy.getPlugin('Dashboard').closeModal();
            }
          });
      })
      .catch(error => {
        // Hide waiting overlay
        this.hideWaitingOverlay();

        console.error("Error creating file:", error);
        if (uppy && uppy.getPlugin('Dashboard')) {
          uppy.getPlugin('Dashboard').closeModal();
        }
      });
  }

  // Method to check if an image URL is accessible with multiple retries
  checkImageUrlAccessibility(url) {
    return true
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 20;
      const intervalMs = 2000;

      const checkWithRetry = () => {
        attempts++;
        console.log(`Checking image accessibility, attempt ${attempts} of ${maxAttempts}: ${url}`);

        this.updateWaitingMessage(`Ожидание загрузки... ${attempts}/${maxAttempts}`);

        const img = new Image();
        img.onload = function () {
          console.log(`Image accessible after ${attempts} attempts: ${url}`);
          resolve(true);
        };
        img.onerror = function () {
          if (attempts < maxAttempts) {
            // Try again after interval
            setTimeout(checkWithRetry, intervalMs);
          } else {
            console.error(`Image not accessible after ${maxAttempts} attempts: ${url}`);
            resolve(false);
          }
        };
        img.src = url;
      };

      checkWithRetry();

      // Set an overall timeout in case the checks get stuck
      setTimeout(() => {
        if (attempts < maxAttempts) {
          console.warn(`Overall timeout reached for image accessibility check: ${url}`);
          resolve(true); // Default to accessible after timeout
        }
      }, (maxAttempts + 5) * intervalMs);
    });
  }

  // Create an overlay to show while waiting for image accessibility check
  showWaitingOverlay(message) {
    // Remove any existing overlay first
    this.hideWaitingOverlay();

    const overlay = document.createElement('div');
    overlay.id = 'waiting-overlay';
    overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
      `;

    const messageBox = document.createElement('div');
    messageBox.id = 'waiting-message';
    messageBox.style.cssText = `
        background-color: white;
        padding: 20px;
        border-radius: 8px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        text-align: center;
        max-width: 80%;
      `;

    const spinner = document.createElement('div');
    spinner.style.cssText = `
        border: 4px solid #f3f3f3;
        border-top: 4px solid #3498db;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        animation: spin 2s linear infinite;
        margin: 0 auto 15px auto;
      `;

    // Add the CSS animation for the spinner
    const style = document.createElement('style');
    style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
    document.head.appendChild(style);

    const text = document.createElement('p');
    text.textContent = message;

    messageBox.appendChild(spinner);
    messageBox.appendChild(text);
    overlay.appendChild(messageBox);
    document.body.appendChild(overlay);
  }

  // Update the waiting message
  updateWaitingMessage(message) {
    const messageElement = document.querySelector('#waiting-message p');
    if (messageElement) {
      messageElement.textContent = message;
    }
  }

  // Remove the waiting overlay
  hideWaitingOverlay() {
    const overlay = document.getElementById('waiting-overlay');
    if (overlay) {
      overlay.remove();
    }
  }

  renderDocumentByType(url, fileName) {
    if (!this.checkSupportedFileType(fileName)) {
      this.showUnsupportedFormat(url)
      return
    }

    const fileExtension = this.extractFileExtension(fileName).toLowerCase()

    switch (fileExtension) {
      case 'pdf':
        this.showPdf(url)
        break

      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'tiff':
      case 'tif':
      case 'bmp':
      case 'webp':
        this.showImage(url)
        break

      case 'docx':
        this.showDocx(url)
        break

      case 'doc':
      case 'odt':
      case 'xlsx':
      case 'xls':
      case 'pptx':
      case 'ppt':
        this.showOfficeDocument(url)
        break

      case 'txt':
      case 'html':
      case 'htm':
        this.showTextDocument(url)
        break

      default:
        this.showDoc(url)
        break
    }
  }

  extractFileExtension(fileName) {
    const lastDotIndex = fileName.lastIndexOf('.')
    return lastDotIndex !== -1 ? fileName.substring(lastDotIndex + 1) : ''
  }

  checkSupportedFileType(fileName) {
    const supportedExtensions = [
      'pdf', 'jpg', 'jpeg', 'png', 'gif', 'tiff', 'tif', 'bmp', 'webp',
      'txt', 'html', 'htm', 'docx', 'doc', 'odt', 'xlsx', 'xls', 'pptx', 'ppt'
    ]
    const fileExtension = this.extractFileExtension(fileName).toLowerCase()
    return supportedExtensions.includes(fileExtension)
  }

  showDocx(url) {
    this.showLoading(true)
    this.docContainerTarget.classList.remove('d-none')
    this.titleTarget.textContent = 'DOCX - ' + this.fileName
    this.startButtonTarget.classList.add('d-none')

    fetch(url)
      .then(response => response.arrayBuffer())
      .then(arrayBuffer => {
        return mammoth.convertToHtml({ arrayBuffer: arrayBuffer })
      })
      .then(result => {
        this.showLoading(false)

        // Create container for DOCX content
        const docxViewHtml = `
          <div class="document-viewer-content" style="padding: 20px; min-height: 70vh; background: white; border: 1px solid #ccc; overflow-y: auto;">
            ${result.value}
          </div>
          ${result.messages.length > 0 ? `
            <div class="alert alert-warning mt-2">
              <small>Conversion warnings: ${result.messages.map(m => m.message).join(', ')}</small>
            </div>
          ` : ''}
        `

        this.docContainerTarget.innerHTML = docxViewHtml
      })
      .catch(error => {
        console.error('Error loading DOCX:', error)
        this.showLoading(false)

        const errorHtml = `
          <div class="text-center text-danger" style="padding: 40px;">
            <i class="fas fa-exclamation-triangle fa-3x mb-3"></i>
            <h5>Error loading DOCX file</h5>
            <p>${error.message}</p>
            <button class="btn btn-primary mt-2" data-action="click->document-modal#handleDownload" data-download-url="${url}" data-download-filename="${this.fileName}">
              <i class="fas fa-download me-2"></i>
              Download File
            </button>
          </div>
        `

        this.docContainerTarget.innerHTML = errorHtml
      })
  }

  showOfficeDocument(url) {
    // For Office documents that can't be previewed directly
    this.showLoading(false)
    this.docContainerTarget.classList.remove('d-none')

    // Create a custom view for office documents
    const officeViewHtml = `
      <div class="text-center" style="padding: 40px;">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">
              <i class="fas fa-file-alt fa-3x mb-3 text-primary"></i>
              <br />
              ${this.fileName}
            </h5>
            <p class="card-text text-muted">
              Preview not available for this file format
            </p>
            <button class="btn btn-primary mt-2" data-action="click->document-modal#handleDownload" data-download-url="${url}" data-download-filename="${this.fileName}">
              <i class="fas fa-download me-2"></i>
              Download File
            </button>
          </div>
        </div>
      </div>
    `

    this.docContainerTarget.innerHTML = officeViewHtml
    this.titleTarget.textContent = 'Документ - ' + this.fileName
    this.startButtonTarget.classList.add('d-none')
  }

  showTextDocument(url) {
    // For text/HTML documents, show in iframe
    this.showLoading(false)
    this.docContainerTarget.classList.remove('d-none')

    const iframeHtml = `
      <iframe 
        src="${url}" 
        style="width: 100%; min-height: 70vh; border: none; background-color: white;"
        title="${this.fileName}"
        sandbox="allow-same-origin">
      </iframe>
    `

    this.docContainerTarget.innerHTML = iframeHtml
    this.titleTarget.textContent = 'Text Document - ' + this.fileName
    this.startButtonTarget.classList.add('d-none')
  }

  showUnsupportedFormat(url) {
    this.showLoading(false)
    this.docContainerTarget.classList.remove('d-none')

    const unsupportedViewHtml = `
      <div class="text-center" style="padding: 40px;">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">
              <i class="fas fa-file fa-3x mb-3 text-warning"></i>
              <br />
              ${this.fileName}
            </h5>
            <p class="card-text text-danger">
              File format not supported for preview
            </p>
            <button class="btn btn-primary mt-2" data-action="click->document-modal#handleDownload" data-download-url="${url}" data-download-filename="${this.fileName}">
              <i class="fas fa-download me-2"></i>
              Download File
            </button>
          </div>
        </div>
      </div>
    `

    this.docContainerTarget.innerHTML = unsupportedViewHtml
    this.titleTarget.textContent = 'Unsupported Format - ' + this.fileName
    this.startButtonTarget.classList.add('d-none')
  }

  async loadPdfDocument(url) {
    try {
      const pdf = await pdfjsLib.getDocument(url).promise

      this.pdfDocument = pdf
      this.currentPage = 1
      this.scale = 1.0

      this.showLoading(false)

      // Update page info
      document.getElementById('pdf-page-info').textContent = `Page 1 of ${pdf.numPages}`

      // Enable/disable navigation buttons
      document.getElementById('pdf-prev').disabled = true
      document.getElementById('pdf-next').disabled = pdf.numPages <= 1

      // Set up event listeners
      this.setupPdfControls()

      // Render first page
      await this.renderPdfPage(1)

    } catch (error) {
      console.error('Error loading PDF:', error)
      this.showLoading(false)

      const errorHtml = `
        <div class="text-center text-danger" style="padding: 40px;">
          <i class="fas fa-exclamation-triangle fa-3x mb-3"></i>
          <h5>Error loading PDF</h5>
          <p>${error.message}</p>
          <button class="btn btn-primary mt-2" data-action="click->document-modal#handleDownload" data-download-url="${url}" data-download-filename="${this.fileName}">
            <i class="fas fa-download me-2"></i>
            Download PDF
          </button>
        </div>
      `

      document.getElementById('pdf-viewer-container').innerHTML = errorHtml
    }
  }

  async renderPdfPage(pageNumber) {
    if (!this.pdfDocument) return

    try {
      const page = await this.pdfDocument.getPage(pageNumber)
      const viewport = page.getViewport({ scale: this.scale })

      // Create canvas
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')

      canvas.height = viewport.height
      canvas.width = viewport.width
      canvas.style.display = 'block'
      canvas.style.margin = '0 auto'
      canvas.style.maxWidth = '100%'

      // Clear container and add canvas
      const container = document.getElementById('pdf-viewer-container')
      container.innerHTML = ''
      container.appendChild(canvas)

      // Render page
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise

      this.currentPage = pageNumber

    } catch (error) {
      console.error('Error rendering PDF page:', error)
    }
  }

  setupPdfControls() {
    const prevBtn = document.getElementById('pdf-prev')
    const nextBtn = document.getElementById('pdf-next')
    const zoomInBtn = document.getElementById('pdf-zoom-in')
    const zoomOutBtn = document.getElementById('pdf-zoom-out')
    const fitWidthBtn = document.getElementById('pdf-fit-width')

    // Remove existing listeners
    prevBtn.replaceWith(prevBtn.cloneNode(true))
    nextBtn.replaceWith(nextBtn.cloneNode(true))
    zoomInBtn.replaceWith(zoomInBtn.cloneNode(true))
    zoomOutBtn.replaceWith(zoomOutBtn.cloneNode(true))
    fitWidthBtn.replaceWith(fitWidthBtn.cloneNode(true))

    // Get fresh references
    const newPrevBtn = document.getElementById('pdf-prev')
    const newNextBtn = document.getElementById('pdf-next')
    const newZoomInBtn = document.getElementById('pdf-zoom-in')
    const newZoomOutBtn = document.getElementById('pdf-zoom-out')
    const newFitWidthBtn = document.getElementById('pdf-fit-width')

    // Add event listeners
    newPrevBtn.addEventListener('click', () => this.previousPage())
    newNextBtn.addEventListener('click', () => this.nextPage())
    newZoomInBtn.addEventListener('click', () => this.zoomIn())
    newZoomOutBtn.addEventListener('click', () => this.zoomOut())
    newFitWidthBtn.addEventListener('click', () => this.fitWidth())
  }

  async previousPage() {
    if (this.currentPage > 1) {
      await this.renderPdfPage(this.currentPage - 1)
      this.updatePdfNavigation()
    }
  }

  async nextPage() {
    if (this.currentPage < this.pdfDocument.numPages) {
      await this.renderPdfPage(this.currentPage + 1)
      this.updatePdfNavigation()
    }
  }

  async zoomIn() {
    this.scale = Math.min(this.scale * 1.2, 3.0)
    await this.renderPdfPage(this.currentPage)
  }

  async zoomOut() {
    this.scale = Math.max(this.scale / 1.2, 0.5)
    await this.renderPdfPage(this.currentPage)
  }

  async fitWidth() {
    const container = document.getElementById('pdf-viewer-container')
    this.scale = (container.clientWidth - 40) / (this.scale * container.clientWidth) * this.scale
    await this.renderPdfPage(this.currentPage)
  }

  updatePdfNavigation() {
    if (!this.pdfDocument) return

    document.getElementById('pdf-page-info').textContent =
      `Page ${this.currentPage} of ${this.pdfDocument.numPages}`

    document.getElementById('pdf-prev').disabled = this.currentPage <= 1
    document.getElementById('pdf-next').disabled = this.currentPage >= this.pdfDocument.numPages
  }

  downloadFile(url, fileName) {
    // Create a temporary anchor element to trigger download with correct filename
    const link = document.createElement('a')
    link.href = url
    link.download = fileName || this.fileName
    link.style.display = 'none'

    // For cross-origin URLs, we need to fetch and create blob
    if (this.isCrossOrigin(url)) {
      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob)
          link.href = blobUrl
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          // Clean up blob URL
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100)
        })
        .catch(error => {
          console.error('Download failed:', error)
          // Fallback to direct link
          link.href = url
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
    } else {
      // Same origin, direct download should work
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  isCrossOrigin(url) {
    try {
      const urlObj = new URL(url, window.location.href)
      return urlObj.origin !== window.location.origin
    } catch (error) {
      return false
    }
  }

  handleDownload(event) {
    const url = event.currentTarget.dataset.downloadUrl
    const filename = event.currentTarget.dataset.downloadFilename
    this.downloadFile(url, filename)
  }
}
